import { debounce } from "../utils";
import { IMessagingService } from "../services";
import { Component } from "./component";

class TableComponent extends Component {
    private table: HTMLElement;
    private tableRows: HTMLTableRowElement[];
    constructor(componentEl: HTMLElement, messagingService: IMessagingService) {
        super(componentEl, messagingService);

        this.table = componentEl;
        this.tableRows = Array.from(this.table.querySelectorAll('tr'));

        // Exit out if a nested table or no rows
        if (this.table.parentElement.closest('table') || this.tableRows.length < 1) return;

        // Check for complex table structures, these won't work as collapsed
        if (this.table.querySelector('[colspan][rowspan]') || this.tableRows[0].querySelector('[rowspan]')) {
            this.table.classList.remove("table-collapsed");
        }
        // Prepare for collapsed table type...
        if (this.table.classList.contains("table-collapsed")) {
            this.table.classList.remove("table-sticky"); // We don't need/want sticky on collapsed type tables, even if user has selected both
            this.generateCollapsedHeadings();
        } 
        // else prepare for sticky (or other) table types
        else {
            // Trigger behaviours on page load and then on window resize(s)
            this.calculateBehaviourOnResize();
            window.addEventListener("resize", debounce(() => {
                this.calculateBehaviourOnResize();
            }, 100));
        }
    }

    private generateCollapsedHeadings() {
        // Grab headings text as an array, duplicating where required (for colspans)
        const headerTextNodes = Array.from(this.tableRows[0].querySelectorAll('th, td')).map((node: HTMLElement) => {
            const colspan = Number(node.getAttribute('colspan'));
            const innerText = node.innerText ?? "";
            return colspan ? Array(colspan).fill(innerText) : innerText;
        }).flat();
        this.tableRows[0].classList.add('table-collapsed__headings');
        // Apply headings text to each row's cells as data attributes (skip first row)
        let rowSpanStore = [];
        this.tableRows.slice(1).forEach(row => {
            let cellColspan = 0;
            let headers = [...headerTextNodes]; // Take a copy
            row.querySelectorAll('th, td').forEach((cell, index) => {
                if (rowSpanStore[index]) {
                    headers.splice(index, 1);
                    rowSpanStore[index] = rowSpanStore[index] - 1;
                    if (rowSpanStore[index] < 1) {
                        rowSpanStore.splice(index, 1);
                    }
                }
                const colspan = cell.getAttribute('colspan');
                const rowSpan = cell.getAttribute('rowspan');
                if (rowSpan) {
                    rowSpanStore[index] = Number(rowSpan) - 1;
                }
                cell.setAttribute('data-heading', headers[index + cellColspan]);
                cellColspan += colspan ? Number(colspan) - 1 : 0;
            });
        });
    }

    private calculateBehaviourOnResize() {
        const {scrollWidth: tableScrollWidth} = this.table;
        const {offsetWidth: parentWidth } = this.table.parentElement; // Fallback if getComputedStyle fails to return
        const tableMaxWidth = getComputedStyle(this.table).getPropertyValue('max-width');
        const maxWidthCalc = tableMaxWidth.includes('px') && !tableMaxWidth.includes('calc') ? parseInt(tableMaxWidth) : parentWidth;
        this.setBehaviour("scroll", tableScrollWidth > maxWidthCalc);
    }

    private setBehaviour(behaviour: string, active: boolean) {
        behaviour && active ? this.table.classList.add(`table-${behaviour}`) : this.table.classList.remove(`table-${behaviour}`);
    }
}

export { TableComponent };